import {
  MUTABLE_PROJECT_STATUS,
  setMutableProjectStatus,
} from './projects.handler';
import { EXTRACTION_SOURCE_CONTENT_TYPES } from '../../features/extraction-sources/extraction-sources.models';
import {
  findSourceFilesByProjectId,
  addSourceFile,
  findExtractionDiffByProjectId,
  findApplyResultByProjectId,
  MOCK_EXECUTION_RUN_PUBLIC_IDS,
} from '../mock-data/extraction-sources.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type {
  ApplyExtractionApiRequest,
  ApplyExtractionApiResponse,
  ExtractionDiffApiResponse,
  ExtractionSourceListApiResponse,
  SourceFileApiResponse,
  SourceFileUploadUrlApiRequest,
  SourceFileUploadUrlApiResponse,
} from '../../features/extraction-sources/extraction-sources.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /projects/:projectId/extraction-sources* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス
 */
export function handleExtractionSourcesRequest(
  method: string,
  path: string,
  body: unknown,
  searchParams: URLSearchParams,
): Observable<HttpResponse<unknown>> | null {
  // POST /projects/:projectId/extraction-sources/upload-url - アップロードURL取得
  const uploadUrlMatch =
    /^\/projects\/([^/]+)\/extraction-sources\/upload-url$/.exec(path);
  if (method === 'POST' && uploadUrlMatch) {
    const projectId = uploadUrlMatch[1];
    return handleGetUploadUrl(projectId, body as SourceFileUploadUrlApiRequest);
  }

  // GET /projects/:projectId/extraction-sources/diff - 差分取得
  const diffMatch = /^\/projects\/([^/]+)\/extraction-sources\/diff$/.exec(
    path,
  );
  if (method === 'GET' && diffMatch) {
    const projectId = diffMatch[1];
    return handleGetDiff(projectId, searchParams);
  }

  // POST /projects/:projectId/extraction-sources/apply - 適用
  const applyMatch = /^\/projects\/([^/]+)\/extraction-sources\/apply$/.exec(
    path,
  );
  if (method === 'POST' && applyMatch) {
    const projectId = applyMatch[1];
    return handleApplyExtraction(projectId, body as ApplyExtractionApiRequest);
  }

  // GET /projects/:projectId/extraction-sources - 抽出ソース一覧取得
  const getListMatch = /^\/projects\/([^/]+)\/extraction-sources$/.exec(path);
  if (method === 'GET' && getListMatch) {
    const projectId = getListMatch[1];
    return handleGetExtractionSources(projectId);
  }

  return null;
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/extraction-sources - 抽出ソース一覧取得
 */
function handleGetExtractionSources(
  projectId: string,
): Observable<HttpResponse<ExtractionSourceListApiResponse>> {
  const items = findSourceFilesByProjectId(projectId);
  return createMockResponse({ items, nextCursor: null });
}

/**
 * POST /api/v0/tenants/:tenantId/projects/:projectId/extraction-sources/upload-url - アップロードURL取得
 * openapi 仕様: 既存の抽出ソースは削除の上、新規作成（重複ハッシュでも常に上書き）
 */
function handleGetUploadUrl(
  projectId: string,
  request: SourceFileUploadUrlApiRequest,
): Observable<
  HttpResponse<SourceFileUploadUrlApiResponse | { message: string }>
> {
  if (!request.fileName || !request.fileHash) {
    return createMockErrorResponse('fileName と fileHash は必須です', 400);
  }

  // contentType の検証（許可 2 値以外は 400）
  if (
    !(EXTRACTION_SOURCE_CONTENT_TYPES as readonly string[]).includes(
      request.contentType,
    )
  ) {
    return createMockErrorResponse(
      `contentType は ${EXTRACTION_SOURCE_CONTENT_TYPES.join(' または ')} のみ許可されています`,
      400,
    );
  }

  // 新規ファイルIDを生成
  const publicId = `source-file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // 新規ソースファイルを追加（既存ファイルは addSourceFile 内で削除される）
  const newFile: SourceFileApiResponse = {
    publicId,
    fileName: request.fileName,
    fileHash: request.fileHash,
    uploadStatus: 'uploading',
    ocrStatus: 'idle',
    // OCR 未実行なので失敗理由は持たない（Issue #475 Phase 3）
    ocrFailedReason: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  addSourceFile(projectId, newFile);

  // アップロード成功後、プロジェクトのステータスを processing にセットする（ポーリング開始のトリガー）
  // refreshAfterExtractionSourceUpload で GET /projects/:id が呼ばれた際に processing を返し、
  // ポーリング開始条件（extractionStatus === 'processing'）を満たすようにする
  setMutableProjectStatus(projectId, 'processing', 'processing');

  // Mock 用の S3 署名付き URL
  const uploadUrl = `https://mock-s3-bucket.s3.ap-northeast-1.amazonaws.com/uploads/${projectId}/${publicId}?X-Amz-Signature=mock`;

  return createMockResponse<SourceFileUploadUrlApiResponse>({
    publicId,
    fileName: request.fileName,
    uploadUrl,
    expiresIn: 3600,
  });
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/extraction-sources/diff - 差分取得
 * extractionStatus が ready_to_apply でない場合は 422 を返す
 */
function handleGetDiff(
  projectId: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<ExtractionDiffApiResponse | { message: string }>> {
  const executionRunPublicId = searchParams.get('executionRunPublicId');

  // executionRunPublicId の検証
  const expectedRunId = MOCK_EXECUTION_RUN_PUBLIC_IDS[projectId];
  if (!executionRunPublicId || executionRunPublicId !== expectedRunId) {
    return createMockErrorResponse(
      '有効な executionRunPublicId が指定されていません',
      422,
    );
  }

  // MUTABLE_PROJECT_STATUS を参照して extractionStatus を確認
  const mutableEntry = MUTABLE_PROJECT_STATUS[projectId] as
    | { extractionStatus: string }
    | undefined;
  const extractionStatus = mutableEntry?.extractionStatus;

  // extractionStatus が ready_to_apply でない場合は 422
  if (extractionStatus !== 'ready_to_apply') {
    return createMockErrorResponse(
      '差分取得は ready_to_apply 状態のみ実行可能です',
      422,
    );
  }

  return createMockResponse<ExtractionDiffApiResponse>(
    findExtractionDiffByProjectId(projectId),
  );
}

/**
 * POST /api/v0/tenants/:tenantId/projects/:projectId/extraction-sources/apply - 適用
 * apply 後に extractionStatus を completed に遷移させる
 */
function handleApplyExtraction(
  projectId: string,
  body: ApplyExtractionApiRequest,
): Observable<HttpResponse<ApplyExtractionApiResponse | { message: string }>> {
  const { executionRunPublicId } = body;

  // executionRunPublicId の検証
  const expectedRunId = MOCK_EXECUTION_RUN_PUBLIC_IDS[projectId];
  if (!executionRunPublicId || executionRunPublicId !== expectedRunId) {
    return createMockErrorResponse(
      '有効な executionRunPublicId が指定されていません',
      422,
    );
  }

  // MUTABLE_PROJECT_STATUS の extractionStatus を completed に遷移させる
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (MUTABLE_PROJECT_STATUS[projectId]) {
    MUTABLE_PROJECT_STATUS[projectId].extractionStatus = 'completed';
  }

  return createMockResponse<ApplyExtractionApiResponse>(
    findApplyResultByProjectId(projectId),
  );
}
